import { supabase } from './supabaseClient'

// The user's actual gym program — a push/pull/legs split. Reuses the
// existing session_type enum values as opaque keys (day1/day2/day3) so
// the DB schema stays unchanged; the user-visible labels are translated
// as Push / Pull / Legs in the locale files.
//
// Each exercise stores the targeted muscle group + equipment used in the
// gym, so they line up with the photos and existing exercise UI. The
// per-session sets/reps/rest are entered at log time — the seed only
// defines what's available to pick.
export const DEFAULT_EXERCISES = [
  // Day 1 — Chest + Back
  { name: 'Incline Chest Press (Machine)',   muscle_group: 'chest',     equipment_type: 'machine',    day: 'day1_chest_back' },
  { name: 'Chest Press (Machine)',           muscle_group: 'chest',     equipment_type: 'machine',    day: 'day1_chest_back' },
  { name: 'Decline Chest Press (Machine)',   muscle_group: 'chest',     equipment_type: 'machine',    day: 'day1_chest_back' },
  { name: 'Pec Deck / Chest Fly (Machine)',  muscle_group: 'chest',     equipment_type: 'machine',    day: 'day1_chest_back' },
  { name: 'Wide Grip Lat Pulldown (Machine)',muscle_group: 'back',      equipment_type: 'machine',    day: 'day1_chest_back' },
  { name: 'Row (Machine)',                   muscle_group: 'back',      equipment_type: 'machine',    day: 'day1_chest_back' },
  { name: 'Low Row (Machine)',               muscle_group: 'back',      equipment_type: 'machine',    day: 'day1_chest_back' },
  { name: 'Chest-Supported T-Bar Row',       muscle_group: 'back',      equipment_type: 'machine',    day: 'day1_chest_back' },
  { name: 'Lat Pulldown (Machine)',          muscle_group: 'back',      equipment_type: 'cable',      day: 'day1_chest_back' },
  { name: 'Cable Row (Close Grip)',          muscle_group: 'back',      equipment_type: 'cable',      day: 'day1_chest_back' },
  { name: 'Hyperextensions',                 muscle_group: 'back',      equipment_type: 'bodyweight', day: 'day1_chest_back' },

  // Day 2 — Shoulders + Biceps + Triceps
  { name: 'Shoulder Press (Machine)',        muscle_group: 'shoulders', equipment_type: 'machine',    day: 'day2_arms_shoulders' },
  { name: 'Lateral Raise (Machine)',         muscle_group: 'shoulders', equipment_type: 'machine',    day: 'day2_arms_shoulders' },
  { name: 'Lateral Raise (Dumbbell)',        muscle_group: 'shoulders', equipment_type: 'dumbbell',   day: 'day2_arms_shoulders' },
  { name: 'Front Raise (Dumbbell)',          muscle_group: 'shoulders', equipment_type: 'dumbbell',   day: 'day2_arms_shoulders' },
  { name: 'Rear Delt Fly (Machine)',         muscle_group: 'shoulders', equipment_type: 'machine',    day: 'day2_arms_shoulders' },
  { name: 'Reverse Pec Deck (Elbow Pads)',   muscle_group: 'shoulders', equipment_type: 'machine',    day: 'day2_arms_shoulders' },
  { name: 'Chest-Supported Rear Delt (Machine)', muscle_group: 'shoulders', equipment_type: 'machine', day: 'day2_arms_shoulders' },
  { name: 'Preacher Curl (Machine)',         muscle_group: 'biceps',    equipment_type: 'machine',    day: 'day2_arms_shoulders' },
  { name: 'Barbell Curl',                    muscle_group: 'biceps',    equipment_type: 'barbell',    day: 'day2_arms_shoulders' },
  { name: 'Drag Curl (Barbell)',             muscle_group: 'biceps',    equipment_type: 'barbell',    day: 'day2_arms_shoulders' },
  { name: 'Drag Curl (Dumbbell)',            muscle_group: 'biceps',    equipment_type: 'dumbbell',   day: 'day2_arms_shoulders' },
  { name: 'Hammer Curl (Dumbbell)',          muscle_group: 'biceps',    equipment_type: 'dumbbell',   day: 'day2_arms_shoulders' },
  { name: 'Triceps Pushdown (Cable)',        muscle_group: 'triceps',   equipment_type: 'cable',      day: 'day2_arms_shoulders' },
  { name: 'Overhead Cable Extension',        muscle_group: 'triceps',   equipment_type: 'cable',      day: 'day2_arms_shoulders' },

  // Day 3 — Legs (quads / hamstrings / glutes / calves / core)
  // calves don't have their own muscle_group enum value; mapping to
  // quads as the closest lower-body classification.
  { name: 'Leg Press (Machine)',             muscle_group: 'quads',      equipment_type: 'machine',    day: 'day3_legs' },
  { name: 'Romanian Deadlift (Dumbbells)',   muscle_group: 'hamstrings', equipment_type: 'dumbbell',   day: 'day3_legs' },
  { name: 'Leg Extension (Machine)',         muscle_group: 'quads',      equipment_type: 'machine',    day: 'day3_legs' },
  { name: 'Leg Curl (Machine)',              muscle_group: 'hamstrings', equipment_type: 'machine',    day: 'day3_legs' },
  { name: 'Seated Calf Raise (Machine)',     muscle_group: 'quads',      equipment_type: 'machine',    day: 'day3_legs' },
  { name: 'Plank',                           muscle_group: 'core',       equipment_type: 'bodyweight', day: 'day3_legs' },
]

// Seeds default exercises for the user and stores the day-template mapping
// on user_profiles.workout_templates. Row-level idempotent: inserts only
// exercises whose names don't already exist for this user, then appends
// the new IDs to the matching day template (preserving any customizations).
export async function seedDefaults(userId) {
  if (!userId) return { ok: false, reason: 'no-user' }

  // 1) Snapshot the user's existing exercises so we can diff by name.
  const { data: existingRows, error: existingErr } = await supabase
    .from('exercises')
    .select('id, name')
    .eq('user_id', userId)
  if (existingErr) return { ok: false, reason: existingErr.message }

  const byName = new Map((existingRows || []).map((r) => [r.name, r.id]))
  const firstRun = (existingRows || []).length === 0

  // 2) Insert any DEFAULT_EXERCISES whose names aren't already present.
  const missing = DEFAULT_EXERCISES.filter((def) => !byName.has(def.name))
  let insertedIds = new Set()
  if (missing.length > 0) {
    const rows = missing.map(({ day, ...e }) => ({ ...e, user_id: userId }))
    const { data: inserted, error: insertErr } = await supabase
      .from('exercises')
      .insert(rows)
      .select('id, name')
    if (insertErr) return { ok: false, reason: insertErr.message }
    for (const r of inserted || []) {
      byName.set(r.name, r.id)
      insertedIds.add(r.id)
    }
  }

  // 3) Read the existing templates so we can merge rather than clobber.
  const { data: profile, error: profileErr } = await supabase
    .from('user_profiles')
    .select('workout_templates')
    .eq('user_id', userId)
    .maybeSingle()
  if (profileErr) return { ok: false, reason: profileErr.message }
  const existingTemplates =
    profile?.workout_templates && typeof profile.workout_templates === 'object'
      ? profile.workout_templates
      : null

  // 4) On first run, build the whole template map from scratch. Otherwise,
  //    append only the *newly* inserted IDs to their respective day arrays
  //    so any reordering / removals the user did are preserved.
  let nextTemplates
  if (!existingTemplates) {
    nextTemplates = { day1_chest_back: [], day2_arms_shoulders: [], day3_legs: [] }
    for (const def of DEFAULT_EXERCISES) {
      const id = byName.get(def.name)
      if (id && nextTemplates[def.day]) nextTemplates[def.day].push(id)
    }
  } else {
    nextTemplates = { ...existingTemplates }
    for (const def of DEFAULT_EXERCISES) {
      const id = byName.get(def.name)
      if (!id || !insertedIds.has(id)) continue
      if (!Array.isArray(nextTemplates[def.day])) nextTemplates[def.day] = []
      if (!nextTemplates[def.day].includes(id)) nextTemplates[def.day].push(id)
    }
  }

  const templatesChanged =
    !existingTemplates ||
    JSON.stringify(existingTemplates) !== JSON.stringify(nextTemplates)

  if (templatesChanged) {
    const { error: tplErr } = await supabase
      .from('user_profiles')
      .upsert(
        { user_id: userId, workout_templates: nextTemplates, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      )
    if (tplErr) return { ok: false, reason: tplErr.message }
  }

  if (insertedIds.size === 0 && !templatesChanged) {
    return { ok: true, reason: 'already-up-to-date', count: 0 }
  }
  return { ok: true, reason: firstRun ? 'seeded' : 'synced', count: insertedIds.size }
}
